import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { NAV_ITEMS } from '../constants';
import Logo from './Logo';

interface NavLink {
  label: string;
  href: string;
  children?: {
    label: string;
    children?: { label: string; href: string }[];
  }[];
}

const phoneNumber = "584141967028";
const message = "Hola, equipo de Stellionet. Mi nombre es [Nombre] de la empresa [Empresa] en [País]. Me interesa recibir una cotización formal para el servicio de [Servicio: Ej. Redes/Ciberseguridad]. ¿Qué información necesitan de mi parte para empezar?";

const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string>(window.location.hash || '');

  const spySections = useCallback((): void => {
    setIsScrolled(window.scrollY > 20);
    const scrollPosition = window.scrollY + 180;
    let foundSection = false;

    if (window.scrollY < 100) {
      setActiveSection(NAV_ITEMS[0].href);
      return;
    }

    NAV_ITEMS.forEach((item) => {
      const sectionId = item.href.replace('#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        const { offsetTop, offsetHeight } = element;
        if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
          setActiveSection(item.href);
          foundSection = true;
        }
      }
    });

    if (!foundSection) {
      setActiveSection('');
    }
  }, []);

  useEffect(() => {
    spySections();
    window.addEventListener('scroll', spySections, { passive: true });
    return () => window.removeEventListener('scroll', spySections);
  }, [spySections]);

  const smoothScrollTo = (targetPosition: number): void => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 800;
    let startTimestamp: number | null = null;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / duration, 1);
      const ease = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      window.scrollTo(0, startPosition + distance * ease);
      if (elapsed < duration) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  };

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string): void => {
    e.preventDefault();
    const sectionId = href.replace('#', '');
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const targetPosition = elementPosition + window.pageYOffset - offset;
      smoothScrollTo(targetPosition);
      window.history.pushState(null, '', href);
      setActiveSection(href);
    }
    setIsOpen(false);
  };

  return (
    <nav 
      className="fixed w-full z-50 top-6 px-4 transition-all duration-300"
      aria-label="Navegación principal"
    >
      <div className="max-w-7xl mx-auto">
        <div
          className={`flex items-center text-center justify-between mx-auto py-2 px-6 rounded-full border transition-all duration-300 backdrop-blur-md
            ${isScrolled
              ? 'bg-white/95 shadow-xl border-primary/10'
              : 'bg-white/60 border-white/40 shadow-lg shadow-black/5'
            }`}
        >
          <a 
            href="#home" 
            onClick={(e) => scrollToSection(e, '#home')} 
            className="flex items-center gap-2 shrink-0 focus-visible:outline-secondary rounded-lg"
            aria-label="Ir al inicio"
          >
            <Logo className="w-32 h-12" />
          </a>

          <div
            className="hidden md:flex items-center gap-1 p-1 rounded-full border border-primary/5"
            role="menubar"
          >
            {NAV_ITEMS.map((item: NavLink) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-300
                  ${activeSection === item.href 
                    ? 'bg-secondary text-white shadow-md scale-105' 
                    : 'text-primary hover:bg-secondary hover:text-white'
                  }`}
                role="menuitem"
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" aria-label="Solicitar presupuesto por WhatsApp">
              Solicitar Presupuesto
            </a>
          </div>

          <div className="flex md:hidden">
            {/* MEJORA: aria-label dinámico para el botón de menú móvil */}
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className="text-primary p-2" 
              aria-label={isOpen ? "Cerrar menú de navegación" : "Abrir menú de navegación"}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <div 
        className={`md:hidden absolute top-full left-4 right-4 mt-3 transition-all duration-300 
        ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
      >
        <div className="bg-white/95 backdrop-blur-lg border border-primary/10 rounded-2xl shadow-2xl p-6 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                /* MEJORA: py-4 para aumentar el área táctil (Tap Target) en móviles */
                className={`text-lg font-bold px-4 py-4 rounded-full w-fit transition-all duration-300
                  ${activeSection === item.href 
                    ? 'bg-secondary text-white' 
                    : 'text-primary hover:bg-secondary hover:text-white'
                  }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="pt-2">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary inline-block text-center w-full py-4">
              Solicitar Presupuesto
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;